import dayjs from 'dayjs';
import { useCallback, useId, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { useIssuerDividendEventInfiniteListQuery } from '@/modules/issuer-dashboard/queries/dividends.tsx';
import { SelectPaginate, SelectPaginateOption } from 'ui';
import { DividendEventExtendResponse } from 'api';

export type PaymentDividendOption = SelectPaginateOption<DividendEventExtendResponse>;

interface PaymentDividendSelectProps {
  disabled?: boolean;
  error?: string;
  label?: string;
  onChange: (option: PaymentDividendOption | null) => void;
  registerId?: number | null;
  value?: PaymentDividendOption | null;
}

export const PaymentDividendSelect = ({
  disabled = false,
  error,
  label,
  onChange,
  registerId,
  value,
}: PaymentDividendSelectProps) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const [params] = useSearchParams();
  const registerIdFromParams = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  const currentRegisterId = registerId || Number(registerIdFromParams || 0);
  const cacheKey = useId();

  const { fetchNextPage } = useIssuerDividendEventInfiniteListQuery(
    { page: 1, page_size: 15, registerId: currentRegisterId || undefined },
    { disabled: true, cacheKey },
  );

  const mapDividendToOption = useCallback(
    (item: DividendEventExtendResponse): PaymentDividendOption => ({
      label: (
        <div className="flex justify-between pr-2 items-center gap-3">
          <span className="flex gap-1 items-center min-w-0">
            <span className="font-medium shrink-0">#{item.id}</span>
            <span className="truncate">{item.name}</span>
            <span className="text-gray-500 truncate">{item.register_class.class_name}</span>
          </span>
          <span className="text-xs text-gray-500 shrink-0">
            {dayjs(item.date).format('YYYY-MM-DD, HH:mm')}
          </span>
        </div>
      ),
      value: item,
    }),
    [],
  );

  const initialOption = useMemo(() => {
    if (!value?.value) return null;

    return mapDividendToOption(value.value);
  }, [mapDividendToOption, value]);

  const handleFetchNextDividends = useCallback(
    async (page: number): Promise<PaymentDividendOption[]> => {
      const dividendsData = await fetchNextPage({ pageParam: page });
      const dividends = dividendsData?.data?.pages?.at(-1)?.data || [];

      return dividends.map(mapDividendToOption);
    },
    [fetchNextPage, mapDividendToOption],
  );

  return (
    <SelectPaginate
      value={initialOption}
      cacheUniqs={[currentRegisterId, cacheKey]}
      label={label || `Select ${getRegisterTypeStrings('dividend', { capitalize: true })}`}
      error={error}
      onAsync={handleFetchNextDividends}
      onChange={option => onChange(option as PaymentDividendOption | null)}
      isClearable={true}
      disabled={!currentRegisterId || disabled}
    />
  );
};
